import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import {
  MessageSquare, Wand2, Video, Code, GraduationCap, Search, Languages, Mic,
  Trophy, Zap, Sparkles, Crown, History, Film, Download, Users, Image as ImageIcon,
  Globe, Send, MoreHorizontal, Rocket, Store, Flame, ArrowRight, PenLine, Snowflake, Plus, Lock, Trash2
} from 'lucide-react';
import { InstallHomeBanner } from '../components/jixxeo/InstallPrompt';
import PromptChips from '../components/jixxeo/PromptChips';
import { toast } from 'sonner';

const QUICK_TOOLS = [
  { to: '/ai/chat',     label: 'Chat',      Icon: MessageSquare, grad: 'from-violet-500 to-fuchsia-500', premium: false },
  { to: '/ai/image',    label: 'Image',     Icon: ImageIcon,     grad: 'from-pink-500 to-rose-500',       premium: true },
  { to: '/ai/video',    label: 'Video',     Icon: Video,         grad: 'from-red-500 to-orange-500',       premium: true },
  { to: '/ai/writing',  label: 'Writing',   Icon: PenLine,       grad: 'from-emerald-400 to-teal-500',    premium: false },
  { to: '/ai/code',     label: 'Code',      Icon: Code,          grad: 'from-sky-500 to-blue-600',         premium: false },
  { to: '/ai/homework', label: 'Homework',  Icon: GraduationCap, grad: 'from-amber-500 to-orange-500',    premium: false },
  { to: '/ai/voice',    label: 'Voice',     Icon: Mic,           grad: 'from-fuchsia-500 to-purple-500',  premium: false },
  { to: '/ai',          label: 'More',      Icon: MoreHorizontal,grad: 'from-slate-500 to-slate-600',     premium: false },
];

const MISSION_META = {
  chat_5:   { reward: 10 },
  images_3: { reward: 15 },
  create_1: { reward: 20 },
  share_1: { reward: 5 },
};

function levelFromJxp(jxp) {
  // Simple: level = 1 + floor(jxp / 100), progress = jxp % 100
  const total = jxp || 0;
  const level = 1 + Math.floor(total / 100);
  const inLevel = total % 100;
  const pct = Math.min(100, inLevel);
  return { level, inLevel, pct };
}

export default function HomeDashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [missions, setMissions] = useState([]);
  const [feed, setFeed] = useState([]);
  const [myCreations, setMyCreations] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [streak, setStreak] = useState(null);
  const [buyingFreeze, setBuyingFreeze] = useState(false);
  const inputRef = useRef(null);

  const loadStreak = () => api.get('/streak/status').then(r => setStreak(r.data)).catch(() => {});

  useEffect(() => {
    if (!user) return;
    api.get('/missions/today').then(r => setMissions(r.data)).catch(() => {});
    api.get('/feed?tab=trending').then(r => setFeed(r.data.slice(0, 6))).catch(() => {});
    api.get('/creations/mine').then(r => setMyCreations(r.data.slice(0, 10))).catch(() => {});
    loadStreak();
  }, [user]);

  if (!user) return null;

  const { level, pct } = levelFromJxp(user.jxp);
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  })();

  const ask = (searchMode = false) => {
    const q = prompt.trim();
    if (!q) { nav(searchMode ? '/ai/research' : '/ai/chat'); return; }
    const route = searchMode ? '/ai/research' : '/ai/chat';
    nav(`${route}?q=${encodeURIComponent(q)}`);
  };

  const buyFreeze = async () => {
    if (buyingFreeze || !streak) return;
    if ((user?.jxp || 0) < streak.freeze_cost_jxp) {
      toast.error(`Need ${streak.freeze_cost_jxp} JXP to buy a freeze`);
      return;
    }
    if (streak.freeze_count >= streak.freeze_max) {
      toast.error(`You already have the maximum ${streak.freeze_max} freezes`);
      return;
    }
    setBuyingFreeze(true);
    try {
      await api.post('/streak/buy-freeze');
      toast.success('❄️ Streak Freeze added! Your streak is protected.');
      await loadStreak();
      await refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not buy freeze');
    } finally { setBuyingFreeze(false); }
  };

  const pickChip = (p) => {
    setPrompt(p.text);
    // short delay so the input has the new value before we navigate
    setTimeout(() => {
      const route = p.route || '/ai/chat';
      nav(`${route}?q=${encodeURIComponent(p.text)}`);
    }, 50);
  };

  return (
    <div className="space-y-5">
      {/* Greeting */}
      <section className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">
            {greeting}, <span className="gradient-text">{user.display_name}</span> <span className="inline-block animate-pulse">👋</span>
          </h1>
          <p className="text-slate-400 text-sm mt-1">Welcome to Jixxeo AI! What would you like to do today?</p>
        </div>
        <div className="relative hidden sm:block">
          <div className="font-display font-black text-xl bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent italic tracking-tight uppercase leading-none">Keep<br/>Creating!</div>
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-5 bg-gradient-to-r from-fuchsia-500/40 to-cyan-500/40 -z-10 skew-x-12 rounded-sm blur-sm" />
        </div>
      </section>

      {/* Big AI search/ask input */}
      <div data-testid="home-ask-bar" className="relative">
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-violet-500/40 via-fuchsia-500/30 to-cyan-500/40 blur-lg opacity-60 pointer-events-none" />
        <div className="relative rounded-2xl border-2 border-violet-500/50 bg-slate-950/80 backdrop-blur-xl p-2.5 flex items-center gap-2 shadow-xl shadow-violet-500/20">
          <Sparkles className="w-5 h-5 text-violet-300 ml-2 flex-shrink-0" />
          <input
            ref={inputRef}
            data-testid="home-ask-input"
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') ask(false); }}
            placeholder="Ask Jixxeo AI anything…"
            className="flex-1 bg-transparent outline-none text-base placeholder:text-slate-500"
          />
          <Button
            data-testid="home-ask-send"
            size="icon"
            onClick={() => ask(false)}
            className="gradient-brand text-white rounded-full h-10 w-10 shadow-lg shadow-violet-500/40"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Rotating smart-prompt chips */}
      <PromptChips onPick={pickChip} />

      {/* Install banner — right under the ask bar, hidden if installed/dismissed */}
      <InstallHomeBanner />

      {/* Three pill tabs: Image / Video / Search */}
      <section className="grid grid-cols-3 gap-2 sm:gap-3">
        {[
          { to: '/ai/image',    Icon: ImageIcon, label: 'Image',  tid: 'pill-image',  locked: !user?.is_premium },
          { to: '/ai/video',    Icon: Video,     label: 'Video',  tid: 'pill-video',  locked: !user?.is_premium },
          { to: '/ai/research', Icon: Globe,     label: 'Search', tid: 'pill-search', locked: false },
        ].map(p => (
          <Link key={p.to} to={p.locked ? '/subscriptions' : p.to} data-testid={p.tid}
                className="glass rounded-2xl py-3 px-2 flex flex-col items-center gap-1 border-violet-500/20 hover:border-violet-500/60 transition-colors relative">
            {p.locked && (
              <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-amber-500/80 flex items-center justify-center shadow">
                <Lock className="w-3 h-3 text-slate-950" />
              </div>
            )}
            <p.Icon className="w-5 h-5 text-violet-300" />
            <div className="text-[11px] text-slate-400">Jixxeo AI</div>
            <div className="font-semibold text-sm text-violet-200">{p.label}</div>
          </Link>
        ))}
      </section>

      {/* Quick Actions */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-400" />
            <h2 className="font-display text-lg font-bold">Quick Actions</h2>
          </div>
          <Link to="/ai" data-testid="quick-view-all" className="text-xs text-violet-400 hover:underline flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 sm:gap-3">
          {QUICK_TOOLS.map(t => {
            const locked = t.premium && !user?.is_premium;
            const to = locked ? '/subscriptions' : t.to;
            return (
              <Link key={t.label} to={to} data-testid={`quick-${t.label.toLowerCase()}`}
                    className="flex flex-col items-center gap-1.5 group relative">
                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${t.grad} flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform relative`}>
                  <t.Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  {locked && (
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center shadow-lg">
                      <Lock className="w-3 h-3 text-slate-950" />
                    </div>
                  )}
                </div>
                <div className="text-[10px] sm:text-[11px] text-slate-400 leading-tight text-center">Jixxeo AI</div>
                <div className="text-xs sm:text-sm font-semibold -mt-1 text-center">{t.label}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Today's Missions */}
      <section>
        <Card data-testid="missions-card" className="glass p-5 border-violet-500/30 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-violet-500/15 blur-3xl pointer-events-none" />
          <div className="relative flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl gradient-gold flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Trophy className="w-6 h-6 text-slate-950" />
              </div>
              <div>
                <div className="font-display font-black text-lg">Today's Jixxeo AI Missions</div>
                <div className="text-xs text-slate-400">Level {level} · {user.jxp || 0} XP</div>
              </div>
            </div>
            <div className="flex items-center gap-1 text-emerald-300 text-sm font-bold">
              <Flame className="w-4 h-4" /> {user.streak || 0} Day Streak!
            </div>
          </div>
          {/* Streak Freeze row */}
          {streak && (
            <div data-testid="streak-freeze-row" className="relative mt-4 flex items-center justify-between gap-2 rounded-xl bg-cyan-500/10 border border-cyan-400/25 px-3 py-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <Snowflake className="w-4 h-4 text-cyan-300" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold">
                    Streak Freezes: <span data-testid="freeze-count" className="text-cyan-300">{streak.freeze_count}</span>
                    <span className="text-slate-500 text-xs"> / {streak.freeze_max}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">Protects your streak if you miss a day</div>
                </div>
              </div>
              <Button
                data-testid="buy-freeze-btn"
                size="sm"
                variant="outline"
                onClick={buyFreeze}
                disabled={buyingFreeze || streak.freeze_count >= streak.freeze_max || (user?.jxp || 0) < streak.freeze_cost_jxp}
                className="border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10 flex-shrink-0 text-xs"
              >
                {streak.freeze_count >= streak.freeze_max ? 'Max' : (
                  (user?.jxp || 0) < streak.freeze_cost_jxp
                    ? 'Not enough'
                    : <><Plus className="w-3 h-3 mr-1" /> {streak.freeze_cost_jxp} JXP</>
                )}
              </Button>
            </div>
          )}
          <div className="relative mt-4">
            <Progress data-testid="level-progress" value={pct} className="h-2" />
            <div className="text-right text-xs text-slate-400 mt-1">{pct}%</div>
          </div>
          <div className="relative mt-4 space-y-2.5">
            {missions.map(m => (
              <div key={m.key} data-testid={`home-mission-${m.key}`} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0">
                  {m.key === 'chat_5' && <MessageSquare className="w-4 h-4 text-violet-300" />}
                  {m.key === 'images_3' && <ImageIcon className="w-4 h-4 text-pink-300" />}
                  {m.key === 'create_1' && <Rocket className="w-4 h-4 text-emerald-300" />}
                  {m.key === 'share_1' && <Users className="w-4 h-4 text-cyan-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-medium truncate">{m.title}</span>
                    <span className="text-slate-400 text-xs ml-2 whitespace-nowrap">{m.progress}/{m.target}</span>
                  </div>
                  <Progress value={Math.min(100, (m.progress / m.target) * 100)} className="h-1.5 mt-1" />
                </div>
                <div className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${m.completed ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                  {m.completed ? '✓ Done' : `+${m.reward} XP`}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </section>

      {/* Recent Creations */}
      {myCreations.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-pink-400" />
              <h2 className="font-display text-lg font-bold">Your Recent Creations</h2>
            </div>
            <Link to="/profile" data-testid="recent-view-all" className="text-xs text-violet-400 hover:underline flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x">
            {myCreations.map(c => (
              <div key={c.id} data-testid={`recent-${c.id}`} className="group relative flex-shrink-0 w-28 sm:w-32 aspect-square rounded-xl overflow-hidden border border-violet-500/30 snap-start">
                {c.url && <img src={c.url} alt="" className="w-full h-full object-cover" />}
                {c.type === 'video' && <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center"><Video className="w-3 h-3 text-white" /></div>}
                <button
                  data-testid={`recent-delete-${c.id}`}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm('Delete this creation?')) return;
                    try {
                      await api.delete(`/creations/mine/${c.id}`);
                      setMyCreations(prev => prev.filter(x => x.id !== c.id));
                    } catch { /* noop */ }
                  }}
                  className="absolute top-1 left-1 w-6 h-6 rounded-full bg-rose-600/95 text-white sm:opacity-0 sm:group-hover:opacity-100 flex items-center justify-center shadow active:scale-95"
                  aria-label="Delete creation"
                ><Trash2 className="w-3 h-3" /></button>
                <div className="absolute bottom-1 left-1 right-1 text-[9px] text-white font-semibold truncate drop-shadow">Jixxeo AI</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Trending CTA */}
      <section>
        <Link to="/discover" data-testid="trending-cta"
              className="block relative overflow-hidden rounded-2xl p-5 border border-violet-500/30 bg-gradient-to-br from-violet-700/30 via-slate-900 to-cyan-700/30 hover:border-violet-400/60 transition-colors">
          <div className="absolute inset-0 opacity-30 pointer-events-none" style={{backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(236,72,153,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(6,182,212,0.3) 0%, transparent 50%)'}} />
          <div className="relative flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg flex-shrink-0">
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold">Trending on Jixxeo AI</div>
              <div className="text-xs text-slate-300">Discover amazing creations from the Jixxeo AI community!</div>
            </div>
            <Button size="sm" className="gradient-brand text-white hidden sm:inline-flex">Explore Now <ArrowRight className="w-3 h-3 ml-1" /></Button>
          </div>
        </Link>
      </section>
    </div>
  );
}
